// SPDX-License-Identifier: BUSL-1.1; Copyright (c) 2025 Social Connect Labs, Inc.; Licensed under BUSL-1.1 (see LICENSE); Apache-2.0 from 2029-06-11
import {
  cloneForStorage
} from "./chunk-7LIQMR7P.js";
import {
  SCANNER_ERROR_CODES,
  markCurrentDocumentAsRegistered,
  reStorePassportDataWithRightCSCA,
  sdkError,
  storePassportData,
  updateDocumentRegistrationState
} from "./chunk-3A3XD7R6.js";
import {
  getCommitmentTree
} from "./chunk-OTRLGZMA.js";

// src/constants/googleUsat.ts
var GOOGLE_USAT_FAUCET_ENDPOINT = "https://cw3-selfxyz-bridge-625632552981.us-central1.run.app/api/verify";
var GOOGLE_USAT_FAUCET_SCOPE = "celo-mainnet-tether-usat";
var GOOGLE_USAT_FAUCET_APP_NAME = "Google Cloud Web3 Portal";

// src/constants/restrictedApps.ts
var GOOGLE_USAT_FAUCET_POLICY = {
  id: "google-usat-faucet",
  match: {
    endpoint: GOOGLE_USAT_FAUCET_ENDPOINT,
    scope: GOOGLE_USAT_FAUCET_SCOPE,
    appName: GOOGLE_USAT_FAUCET_APP_NAME
  },
  allowedCategories: ["passport", "id_card", "aadhaar"],
  allowMock: false
};
var RESTRICTED_APP_REGISTRY = [GOOGLE_USAT_FAUCET_POLICY];

// src/proving/recoveryValidation.ts
import { isUserRegisteredWithAlternativeCSCA } from "@selfxyz/common/utils/passports/validate";
function getAltCSCA(selfClient, docType) {
  if (docType === "aadhaar" || docType === "kyc") {
    const publicKeys = selfClient.getProtocolState()[docType].public_keys;
    return publicKeys ? Object.fromEntries(publicKeys.map((key) => [key, key])) : {};
  }
  return selfClient.getProtocolState()[docType].alternative_csca;
}
async function finalizeRecoveredDocumentRegistration(selfClient, document, csca) {
  const originalDocument = cloneForStorage(document);
  const originalCatalog = await selfClient.loadDocumentCatalog();
  const originalSelectedDocument = originalCatalog.selectedDocumentId ? originalCatalog.documents.find((doc) => doc.id === originalCatalog.selectedDocumentId) : void 0;
  const originalSelectedDocumentSnapshot = originalSelectedDocument ? {
    id: originalSelectedDocument.id,
    isRegistered: originalSelectedDocument.isRegistered,
    registeredAt: originalSelectedDocument.registeredAt
  } : void 0;
  try {
    if (csca) {
      await reStorePassportDataWithRightCSCA(selfClient, document, csca);
    }
    await markCurrentDocumentAsRegistered(selfClient);
  } catch (error) {
    if (csca) {
      try {
        await storePassportData(selfClient, originalDocument);
      } catch (rollbackError) {
        console.error("Rollback failed while restoring the original document during recovery:", rollbackError);
      }
    }
    if (originalSelectedDocumentSnapshot) {
      try {
        const rollbackCatalog = await selfClient.loadDocumentCatalog();
        rollbackCatalog.selectedDocumentId = originalCatalog.selectedDocumentId;
        const rollbackDocument = rollbackCatalog.documents.find((doc) => doc.id === originalSelectedDocumentSnapshot.id);
        if (rollbackDocument) {
          rollbackDocument.isRegistered = originalSelectedDocumentSnapshot.isRegistered;
          rollbackDocument.registeredAt = originalSelectedDocumentSnapshot.registeredAt;
          await selfClient.saveDocumentCatalog(rollbackCatalog);
        } else {
          await updateDocumentRegistrationState(
            selfClient,
            originalSelectedDocumentSnapshot.id,
            Boolean(originalSelectedDocumentSnapshot.isRegistered)
          );
        }
      } catch (rollbackError) {
        console.error("Rollback failed while restoring the registration flag during recovery:", rollbackError);
      }
    }
    throw error;
  }
}
async function validateRecoverySecretForDocument(selfClient, document, secret) {
  const { isRegistered, csca } = await isUserRegisteredWithAlternativeCSCA(document, secret, {
    getCommitmentTree: (docCategory) => getCommitmentTree(selfClient, docCategory),
    getAltCSCA: (docType) => getAltCSCA(selfClient, docType)
  });
  return {
    isRegistered,
    csca: csca ?? void 0
  };
}

// src/mock/generator.ts
import { generateMockDSC, genMockIdDoc, getSKIPEM, initPassportDataParsing } from "@selfxyz/common";
var formatDateToYYMMDD = (date) => {
  return (date.toISOString().slice(2, 4) + date.toISOString().slice(5, 7) + date.toISOString().slice(8, 10)).toString();
};
var formatDateToDDMMYYYY = (date) => {
  return (date.toISOString().slice(8, 10) + "-" + date.toISOString().slice(5, 7) + "-" + date.toISOString().slice(0, 4)).toString();
};
var getBirthDateFromAge = (age, format = "YYMMDD") => {
  const date = /* @__PURE__ */ new Date();
  date.setFullYear(date.getFullYear() - age);
  return format === "YYMMDD" ? formatDateToYYMMDD(date) : formatDateToDDMMYYYY(date);
};
var getExpiryDateFromYears = (years) => {
  const date = /* @__PURE__ */ new Date();
  date.setFullYear(date.getFullYear() + years);
  return formatDateToYYMMDD(date);
};
async function generateMockDocument({
  age,
  expiryYears,
  isInOfacList,
  selectedAlgorithm,
  selectedCountry,
  selectedDocumentType,
  firstName,
  lastName
}) {
  console.log("generateMockDocument received names:", { firstName, lastName, isInOfacList });
  const ALPHANUMERIC = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const randomBytes = new Uint8Array(9);
  crypto.getRandomValues(randomBytes);
  const randomPassportNumber = Array.from(randomBytes).map((b) => ALPHANUMERIC[b % ALPHANUMERIC.length]).join("");
  const [dgHashAlgo, eContentHashAlgo, signatureTypeForGeneration] = signatureAlgorithmToStrictSignatureAlgorithm[selectedAlgorithm];
  const idDocInput = {
    nationality: selectedCountry,
    idType: selectedDocumentType,
    dgHashAlgo,
    eContentHashAlgo,
    signatureType: signatureTypeForGeneration,
    expiryDate: getExpiryDateFromYears(expiryYears),
    passportNumber: randomPassportNumber,
    sex: "M"
    // Default value
  };
  if (selectedDocumentType === "mock_aadhaar") {
    idDocInput.birthDate = getBirthDateFromAge(age, "DDMMYYYY");
    if (isInOfacList) {
      idDocInput.lastName = lastName || "HENAO MONTOYA";
      idDocInput.firstName = firstName || "ARCANGEL DE JESUS";
      idDocInput.birthDate = "07-10-1954";
    } else {
      if (firstName) idDocInput.firstName = firstName;
      if (lastName) idDocInput.lastName = lastName;
    }
    const result = genMockIdDoc(idDocInput);
    if ("qrData" in result) {
      console.log("Generated Aadhaar qrData:", result.qrData);
      console.log("Generated Aadhaar extractedFields:", result.extractedFields);
    }
    return result;
  }
  let dobForGeneration;
  if (isInOfacList) {
    dobForGeneration = "541007";
    idDocInput.lastName = lastName || "HENAO MONTOYA";
    idDocInput.firstName = firstName || "ARCANGEL DE JESUS";
  } else {
    dobForGeneration = getBirthDateFromAge(age);
    if (firstName) idDocInput.firstName = firstName;
    if (lastName) idDocInput.lastName = lastName;
  }
  idDocInput.birthDate = dobForGeneration;
  let mockDSC, rawMockData;
  try {
    mockDSC = await generateMockDSC(idDocInput.signatureType || "rsa_sha256_65537_2048");
    rawMockData = genMockIdDoc(idDocInput, mockDSC);
  } catch (error) {
    console.warn("Falling back to default mock DSC. Error during mock DSC generation:", error);
    rawMockData = genMockIdDoc(idDocInput);
  }
  const skiPem = await getSKIPEM("staging");
  return initPassportDataParsing(rawMockData, skiPem);
}
var signatureAlgorithmToStrictSignatureAlgorithm = {
  "sha256 rsa 65537 4096": ["sha256", "sha256", "rsa_sha256_65537_4096"],
  "sha1 rsa 65537 2048": ["sha1", "sha1", "rsa_sha1_65537_2048"],
  "sha256 brainpoolP256r1": ["sha256", "sha256", "ecdsa_sha256_brainpoolP256r1_256"],
  "sha384 brainpoolP384r1": ["sha384", "sha384", "ecdsa_sha384_brainpoolP384r1_384"],
  "sha384 secp384r1": ["sha384", "sha384", "ecdsa_sha384_secp384r1_384"],
  "sha256 rsa 65537 2048": ["sha256", "sha256", "rsa_sha256_65537_2048"],
  "sha256 rsa 3 2048": ["sha256", "sha256", "rsa_sha256_3_2048"],
  "sha256 rsa 65537 3072": ["sha256", "sha256", "rsa_sha256_65537_3072"],
  "sha256 rsa 3 4096": ["sha256", "sha256", "rsa_sha256_3_4096"],
  "sha384 rsa 65537 4096": ["sha384", "sha384", "rsa_sha384_65537_4096"],
  "sha512 rsa 65537 2048": ["sha512", "sha512", "rsa_sha512_65537_2048"],
  "sha512 rsa 65537 4096": ["sha512", "sha512", "rsa_sha512_65537_4096"],
  "sha1 rsa 65537 4096": ["sha1", "sha1", "rsa_sha1_65537_4096"],
  "sha256 rsapss 3 2048": ["sha256", "sha256", "rsapss_sha256_3_2048"],
  "sha256 rsapss 3 3072": ["sha256", "sha256", "rsapss_sha256_3_3072"],
  "sha256 rsapss 65537 3072": ["sha256", "sha256", "rsapss_sha256_65537_3072"],
  "sha256 rsapss 65537 4096": ["sha256", "sha256", "rsapss_sha256_65537_4096"],
  "sha384 rsapss 65537 2048": ["sha384", "sha384", "rsapss_sha384_65537_2048"],
  "sha384 rsapss 65537 3072": ["sha384", "sha384", "rsapss_sha384_65537_3072"],
  "sha512 rsapss 65537 2048": ["sha512", "sha512", "rsapss_sha512_65537_2048"],
  "sha512 rsapss 65537 4096": ["sha512", "sha512", "rsapss_sha512_65537_4096"],
  "sha1 secp256r1": ["sha1", "sha1", "ecdsa_sha1_secp256r1_256"],
  "sha224 secp224r1": ["sha224", "sha224", "ecdsa_sha224_secp224r1_224"],
  "sha256 secp256r1": ["sha256", "sha256", "ecdsa_sha256_secp256r1_256"],
  "sha256 secp384r1": ["sha256", "sha256", "ecdsa_sha256_secp384r1_384"],
  "sha1 brainpoolP224r1": ["sha1", "sha1", "ecdsa_sha1_brainpoolP224r1_224"],
  "sha1 brainpoolP256r1": ["sha1", "sha1", "ecdsa_sha1_brainpoolP256r1_256"],
  "sha224 brainpoolP224r1": ["sha224", "sha224", "ecdsa_sha224_brainpoolP224r1_224"],
  "sha256 brainpoolP224r1": ["sha256", "sha256", "ecdsa_sha256_brainpoolP224r1_224"],
  "sha384 brainpoolP256r1": ["sha384", "sha384", "ecdsa_sha384_brainpoolP256r1_256"],
  "sha512 brainpoolP256r1": ["sha512", "sha512", "ecdsa_sha512_brainpoolP256r1_256"],
  "sha512 brainpoolP384r1": ["sha512", "sha512", "ecdsa_sha512_brainpoolP384r1_384"],
  "sha512 poland": ["sha512", "sha512", "rsa_sha256_65537_4096"],
  "not existing": ["sha512", "sha384", "rsa_sha256_65537_4096"]
};

// src/utils/restrictedApps.ts
function findRestrictedAppPolicy(app, registry = RESTRICTED_APP_REGISTRY) {
  for (const policy of registry) {
    if (
      // endpoint === normalizeEndpoint(policy.match.endpoint) &&
      app.scope === policy.match.scope && app.appName === policy.match.appName
    ) {
      return policy;
    }
  }
  return null;
}
function isDocumentEligibleForPolicy(policy, category, isMock) {
  if (!policy.allowedCategories.includes(category)) {
    return false;
  }
  if (isMock === true && !policy.allowMock) {
    return false;
  }
  return true;
}
function hasEligibleAlternativeDocumentForPolicy(policy, docs, excludedDocumentId) {
  for (const [documentId, document] of Object.entries(docs)) {
    if (documentId === excludedDocumentId) continue;
    if (isDocumentEligibleForPolicy(policy, document.data.documentCategory, document.data.mock)) {
      return true;
    }
  }
  return false;
}

// src/utils/googleUsat.ts
var GOOGLE_USAT_FAUCET_IDENTITY = {
  endpoint: GOOGLE_USAT_FAUCET_POLICY.match.endpoint,
  scope: GOOGLE_USAT_FAUCET_POLICY.match.scope,
  appName: GOOGLE_USAT_FAUCET_POLICY.match.appName
};
function isGoogleUsatProofRequest(app, identity = GOOGLE_USAT_FAUCET_IDENTITY) {
  const matched = findRestrictedAppPolicy(app, [
    {
      id: GOOGLE_USAT_FAUCET_POLICY.id,
      match: identity,
      allowedCategories: GOOGLE_USAT_FAUCET_POLICY.allowedCategories,
      allowMock: GOOGLE_USAT_FAUCET_POLICY.allowMock
    }
  ]);
  return matched !== null;
}

// src/validation/document.ts
import { formatMrz, hash } from "@selfxyz/common";
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
var SUPPORTED_HASH_FUNCTIONS = ["sha256", "sha384", "sha512"];
function isSupportedHashAlgorithm(x) {
  return SUPPORTED_HASH_FUNCTIONS.includes(x);
}
function isPassportDataValid(passportData, callbacks = {}) {
  const {
    onPassportDataNull,
    onPassportMetadataNull,
    onDg1HashFunctionNull,
    onEContentHashFunctionNull,
    onSignedAttrHashFunctionNull,
    onDg1HashMismatch,
    onUnsupportedHashAlgorithm,
    onDg1HashMissing
  } = callbacks;
  if (!passportData) {
    onPassportDataNull?.();
    return false;
  }
  const { passportMetadata } = passportData;
  if (!passportMetadata) {
    onPassportMetadataNull?.(passportData);
    return false;
  }
  const { dg1HashFunction, eContentHashFunction, signedAttrHashFunction } = passportMetadata;
  if (!dg1HashFunction) {
    onDg1HashFunctionNull?.(passportData);
    return false;
  }
  if (!eContentHashFunction) {
    onEContentHashFunctionNull?.(passportData);
    return false;
  }
  if (!signedAttrHashFunction) {
    onSignedAttrHashFunctionNull?.(passportData);
    return false;
  }
  const dg1Algo = dg1HashFunction.toLowerCase();
  const eContentAlgo = eContentHashFunction.toLowerCase();
  const signedAttrAlgo = signedAttrHashFunction.toLowerCase();
  if (!isSupportedHashAlgorithm(dg1Algo)) {
    onUnsupportedHashAlgorithm?.("dg1", dg1Algo, passportData);
    return false;
  }
  if (!isSupportedHashAlgorithm(eContentAlgo)) {
    onUnsupportedHashAlgorithm?.("eContent", eContentAlgo, passportData);
    return false;
  }
  if (!isSupportedHashAlgorithm(signedAttrAlgo)) {
    onUnsupportedHashAlgorithm?.("signedAttr", signedAttrAlgo, passportData);
    return false;
  }
  if (!passportData.mrz) {
    return false;
  }
  if (passportData.dg1Hash && passportData.dg1Hash.length > 0) {
    try {
      const hashResult = hash(dg1Algo, formatMrz(passportData.mrz));
      if (!Array.isArray(hashResult) || !hashResult.every((n) => typeof n === "number" && Number.isFinite(n))) {
        return false;
      }
      const expected = hashResult;
      if (!arraysEqual(passportData.dg1Hash, expected)) {
        onDg1HashMismatch?.(passportData);
        return false;
      }
    } catch (e) {
      console.error("Error calculating DG1 hash:", e);
      return false;
    }
  } else {
    onDg1HashMissing?.(passportData);
  }
  return true;
}

// src/adapters/web/shims.ts
var webNFCScannerShim = {
  async scan(_opts) {
    throw sdkError("NFC not supported in web shim", SCANNER_ERROR_CODES.NFC_NOT_SUPPORTED, "scanner");
  }
};

export {
  GOOGLE_USAT_FAUCET_ENDPOINT,
  GOOGLE_USAT_FAUCET_SCOPE,
  GOOGLE_USAT_FAUCET_APP_NAME,
  GOOGLE_USAT_FAUCET_POLICY,
  finalizeRecoveredDocumentRegistration,
  validateRecoverySecretForDocument,
  generateMockDocument,
  signatureAlgorithmToStrictSignatureAlgorithm,
  isDocumentEligibleForPolicy,
  hasEligibleAlternativeDocumentForPolicy,
  isGoogleUsatProofRequest,
  isPassportDataValid,
  webNFCScannerShim
};
//# sourceMappingURL=chunk-PI3UP5TT.js.map